import React, { useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import { canViewPricingData } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import ConfirmModal from '../../../common/Modal/ConfirmModal';
import {
  CUSTOM_WIDGET_TYPES,
  GRAPH_WIDGET_TYPES,
  SINGLE_METRIC_WIDGET_TYPES,
  WIDGET_TYPES,
} from '../../../common/Widgets/constants';
import { getWidgetTypeLabel } from '../../../common/Widgets/utils';
import Checkbox from '../../lib/Checkbox';
import Input from '../../lib/Input';
import { useCommunityViewContext } from '../CommunityViewContext';
import { getAvailableWidgetOptions } from '../widgetOptions';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  gap: 10px;
  flex-grow: 1;
  flex-grow: 1;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  justify-content: end;
  padding: 20px 0 0 0;
  flex-grow: 1;
`;

const SelectionContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding-left: 30px;
  justify-content: space-between;
`;

const SelectionCategoryContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CategoryTitle = styled.div`
  display: flex;
  flex-direction: row;
  font-size: 12px;
  font-family: var(--font-bold);
  color: var(--text-primary);
  border-bottom: 1px solid var(--line-gray);
  padding: 5px 0;
`;

const CategoryTitleLabel = styled.span`
  margin-left: 8px;
`;

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 5px;
`;

interface Props {
  open: boolean;
  onConfirmCreateNew: any;
  onCancel: any;
}

const GRAPH_TABLE_OPTIONS = [...GRAPH_WIDGET_TYPES, WIDGET_TYPES.SWOT_PERFORMANCE, ...CUSTOM_WIDGET_TYPES];
const SINGLE_METRIC_OPTIONS = SINGLE_METRIC_WIDGET_TYPES;

const ViewSaveFilterModal = ({ open, onConfirmCreateNew, onCancel }: Props) => {
  const { community, view } = useCommunityViewContext();
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const showPricingData = canViewPricingData(currentUser);
  const [graphTableOptions, setGraphTableOptions] = useState<number[]>([]);
  const [singleMetricOptions, setSingleMetricOptions] = useState<number[]>([]);
  const [name, setName] = useState<string>('');

  const { availableGraphTableOptions, availableSingleMetricOptions } = useMemo(() => {
    const { availableGraphOptions, availableSingleOptions } = getAvailableWidgetOptions({
      graphOptions: GRAPH_TABLE_OPTIONS,
      singleOptions: SINGLE_METRIC_OPTIONS,
      showPricingData,
      associatedRentalTypes: view?.associated_rental_types,
    });

    return {
      availableGraphTableOptions: availableGraphOptions,
      availableSingleMetricOptions: availableSingleOptions,
    };
  }, [showPricingData, view?.associated_rental_types]);

  useEffect(() => {
    setName('');
    setGraphTableOptions(availableGraphTableOptions);
    setSingleMetricOptions(availableSingleMetricOptions);
  }, [open, availableGraphTableOptions, availableSingleMetricOptions]);

  const showSingleMetricsSection = availableSingleMetricOptions.length > 0;

  const handleConfirm = () => {
    onConfirmCreateNew({ name, widget_types: [...singleMetricOptions, ...graphTableOptions] });
  };

  const handleNameChange = (e: any) => {
    setName(e.target.value);
  };

  const handleSelectAllGraphsTables = (e: any) => {
    if (e.target.checked) {
      setGraphTableOptions(availableGraphTableOptions);
    } else {
      setGraphTableOptions([]);
    }
  };

  const handleSelectAllSingleMetrics = (e: any) => {
    if (e.target.checked) {
      setSingleMetricOptions(availableSingleMetricOptions);
    } else {
      setSingleMetricOptions([]);
    }
  };

  const onGraphTableChange = (values: any) => {
    setGraphTableOptions([...values]);
  };

  const onSingleMetricChange = (values: any) => {
    setSingleMetricOptions([...values]);
  };

  return (
    <ConfirmModal
      open={open}
      title={'Save Filter'}
      description={'You will create a new view from the current filter.'}
      confirmLabel={'Create New'}
      onConfirm={handleConfirm}
      onCancel={onCancel}
      disabled={name.length == 0 || (graphTableOptions.length == 0 && singleMetricOptions.length == 0)}
    >
      <Container>
        <InputContainer>
          <span>View Name</span>
          <Input placeholder={'Enter a name (visible to all communities)'} onChange={handleNameChange} />
        </InputContainer>
        <SelectionContainer>
          <SelectionCategoryContainer>
            <CategoryTitle>
              <Checkbox
                onChange={handleSelectAllGraphsTables}
                checked={graphTableOptions.length === availableGraphTableOptions.length}
              />
              <CategoryTitleLabel>Graphs and Tables</CategoryTitleLabel>
            </CategoryTitle>
            <Checkbox.Group value={graphTableOptions} onChange={onGraphTableChange}>
              <CategoryList>
                {availableGraphTableOptions.map((type) => (
                  <Checkbox key={type} value={type}>
                    {getWidgetTypeLabel(type, community)}
                  </Checkbox>
                ))}
              </CategoryList>
            </Checkbox.Group>
          </SelectionCategoryContainer>
          {showSingleMetricsSection && (
            <SelectionCategoryContainer>
              <CategoryTitle>
                <Checkbox
                  onChange={handleSelectAllSingleMetrics}
                  checked={singleMetricOptions.length === availableSingleMetricOptions.length}
                />
                <CategoryTitleLabel>Single Metrics</CategoryTitleLabel>
              </CategoryTitle>
              <Checkbox.Group value={singleMetricOptions} onChange={onSingleMetricChange}>
                <CategoryList>
                  {availableSingleMetricOptions.map((type) => (
                    <Checkbox key={type} value={type}>
                      {getWidgetTypeLabel(type, community)}
                    </Checkbox>
                  ))}
                </CategoryList>
              </Checkbox.Group>
            </SelectionCategoryContainer>
          )}
        </SelectionContainer>
      </Container>
    </ConfirmModal>
  );
};

export default ViewSaveFilterModal;
